import React, { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import api from '../services/api';
import Modal from './Modal';

// ============================================================
// Carga de las metas del mes (rediseño de Inicio, parte 3). Gerente o admin;
// las ven todos los usuarios de la empresa. Cada meta se prende o apaga
// (apagada = no se guarda) y todas van en la misma moneda (BCV$ o USD).
// ============================================================

export const NOMBRES_META = {
  ventas: { nombre: 'Ventas del mes', ayuda: 'Facturas menos notas de crédito y tickets de caja, sin IVA' },
  cobranza: { nombre: 'Cobranza del mes', ayuda: 'Lo cobrado a clientes en el mes' },
  tope_compras: { nombre: 'Tope de compras', ayuda: 'Facturas de compra sin IVA; avisa si se pasa: más bajo es mejor' },
  ticket_promedio: { nombre: 'Ticket promedio', ayuda: 'Ventas de caja entre tickets, sin IVA' }
};

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

function mesAnterior(anio, mes) {
  return mes === 1 ? { anio: anio - 1, mes: 12 } : { anio, mes: mes - 1 };
}

// En restaurantes la meta de ventas se compara solo con los tickets de caja.
export const ayudaMeta = (tipo, rubro) =>
  tipo === 'ventas' && rubro === 'restaurante' ? 'Tickets de caja, sin IVA' : NOMBRES_META[tipo].ayuda;

export default function EditarMetas({ anio, mes, tipos, rubro, actuales, empresaNombre, onGuardado, onCerrar }) {
  const inicial = () => {
    const v = {};
    for (const t of tipos) {
      const m = actuales.find((x) => x.tipo === t);
      v[t] = { activa: !!m, valor: m ? String(m.valor) : '' };
    }
    return v;
  };
  const [valores, setValores] = useState(inicial);
  const [moneda, setMoneda] = useState(actuales[0]?.moneda || 'BCV');
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [guardando, setGuardando] = useState(false);
  const ant = mesAnterior(anio, mes);

  useEffect(() => { setError(''); }, [valores, moneda]);

  async function copiarAnterior() {
    setAviso('');
    try {
      const { data } = await api.get('/inicio/metas', { params: ant });
      if (data.metas.length === 0) { setAviso(`${MESES[ant.mes - 1][0].toUpperCase()}${MESES[ant.mes - 1].slice(1)} no tiene metas cargadas.`); return; }
      const v = {};
      for (const t of tipos) {
        const m = data.metas.find((x) => x.tipo === t);
        v[t] = { activa: !!m, valor: m ? String(m.valor) : '' };
      }
      setValores(v);
      setMoneda(data.metas[0].moneda);
    } catch {
      setAviso('No se pudieron leer las metas del mes anterior.');
    }
  }

  async function guardar(e) {
    e.preventDefault();
    const metas = {};
    for (const t of tipos) {
      if (!valores[t].activa) continue;
      const n = Number(String(valores[t].valor).replace(',', '.'));
      if (!(n > 0)) { setError(`Escribe un monto mayor que cero en "${NOMBRES_META[t].nombre}" o apágala.`); return; }
      metas[t] = n;
    }
    setGuardando(true);
    try {
      await api.put('/inicio/metas', { anio, mes, moneda, metas });
      onGuardado();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron guardar las metas.');
      setGuardando(false);
    }
  }

  const cambiar = (t, campo, valor) => setValores((v) => ({ ...v, [t]: { ...v[t], [campo]: valor } }));
  const prefijo = moneda === 'USD' ? '$' : 'BCV$';

  return (
    <Modal title={`Metas de ${MESES[mes - 1]}${empresaNombre ? ` · ${empresaNombre}` : ''}`} onClose={onCerrar} maxWidth="max-w-lg">
      <form onSubmit={guardar} className="space-y-4">
        <p className="text-xs text-slate-500">Las ven en su Inicio todos los usuarios de esta empresa.</p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex rounded-md border border-slate-200 p-0.5 text-sm">
            {['BCV', 'USD'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMoneda(m)}
                className={`px-3 py-1 rounded ${moneda === m ? 'bg-primary-600 text-white font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                En {m === 'BCV' ? 'BCV$' : 'USD'}
              </button>
            ))}
          </div>
          <button type="button" onClick={copiarAnterior} className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700">
            <Copy size={15} /> Copiar las de {MESES[ant.mes - 1]}
          </button>
        </div>
        {aviso && <p className="text-xs text-amber-700">{aviso}</p>}
        <div className="space-y-2.5">
          {tipos.map((t) => (
            <div key={t} className={`flex items-center gap-3 rounded-lg border border-slate-200 p-3 ${valores[t].activa ? 'bg-white' : 'bg-slate-50'}`}>
              <input
                type="checkbox"
                checked={valores[t].activa}
                onChange={(e) => cambiar(t, 'activa', e.target.checked)}
                className="h-4 w-4 shrink-0"
                aria-label={`Usar la meta ${NOMBRES_META[t].nombre}`}
              />
              <span className="flex-1 min-w-0">
                <span className={`block text-sm font-semibold ${valores[t].activa ? 'text-slate-800' : 'text-slate-400'}`}>{NOMBRES_META[t].nombre}</span>
                <span className="block text-xs text-slate-500">{ayudaMeta(t, rubro)}</span>
              </span>
              <span className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs text-slate-500">{prefijo}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  disabled={!valores[t].activa}
                  value={valores[t].valor}
                  onChange={(e) => cambiar(t, 'valor', e.target.value)}
                  className="w-32 border rounded-md px-3 py-2 text-right text-sm disabled:bg-slate-100"
                />
              </span>
            </div>
          ))}
        </div>
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className="rounded-md px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
          <button type="submit" disabled={guardando} className="bg-primary-600 text-white rounded-md px-4 py-2 text-sm hover:bg-primary-700 disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar metas'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
